import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CircularProgress,
  Divider,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { apiRequest } from "../../../utils/api";
import { useLoginStatus } from "../../../context/LoginStatusContext";
import { useAlert } from "../../../context/AlertContext";
import { useNotice } from "../../../context/NoticeContext";
import { useInvitation } from "../../../context/InvitationContext";

interface Organization {
  uuid: string;
  name: string;
  role: string;
}

interface Invitation {
  uuid: string;
  organization: Organization;
  role: string;
  invited_by: {
    id: number;
    username: string;
  };
  email: string;
  expires_at: string;
}

const InvitationManagement: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { user } = useLoginStatus();
  const { setAlert } = useAlert();
  const { triggerNotice } = useNotice();
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchInvitations = async () => {
    try {
      setLoading(true);
      const response = await apiRequest<{
        pending_invitations: Invitation[];
        count: number;
      }>("/api/v1/enterprise/users/me/pending-invitations/");

      setInvitations(response.data.pending_invitations || []);
      setLoading(false);
    } catch (error) {
      console.error("Failed to fetch invitations:", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchInvitations();
    }
  }, [user]);

  const { acceptInvitation: contextAcceptInvitation } = useInvitation();

  const handleAccept = async (uuid: string, organizationName: string) => {
    try {
      // コンテキストの拡張されたメソッドを使用
      const success = await contextAcceptInvitation(uuid);

      if (success) {
        setAlert("success", t("preferences.invitation.acceptedSuccess", { organization: organizationName }));
        fetchInvitations();
      } else {
        setAlert("error", t("preferences.invitation.acceptError"));
      }
    } catch (error) {
      console.error("Failed to accept invitation:", error);
      setAlert("error", t("preferences.invitation.acceptError"));
    }
  };

  const handleDecline = async (uuid: string) => {
    try {
      await apiRequest(`/api/v1/enterprise/organizations/invitations/${uuid}/decline/`, "POST");
      setInvitations(invitations.filter((invitation) => invitation.uuid !== uuid));
      triggerNotice("info", t("preferences.invitation.declinedSuccess"));
    } catch (error) {
      console.error("Failed to decline invitation:", error);
      setAlert("error", t("preferences.invitation.declineError"));
    }
  };

  // ロール名の翻訳
  const getRoleName = (role: string) => {
    switch (role) {
      case "owner":
        return t("preferences.enterprise.role.owner");
      case "manager":
        return t("preferences.enterprise.role.manager");
      case "member":
        return t("preferences.enterprise.role.member");
      default:
        return role;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  // ユーザーが既に組織に所属している場合はメッセージを表示
  const isAlreadyInOrganization = !!user?.enterprise;

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isAlreadyInOrganization) {
    return (
      <Typography variant="body1" color="text.secondary" sx={{ p: 2 }}>
        {t("preferences.invitation.alreadyInOrganization")}
      </Typography>
    );
  }

  if (invitations.length === 0) {
    return (
      <Typography variant="body1" color="text.secondary" sx={{ p: 2 }}>
        {t("preferences.invitation.noInvitations")}
      </Typography>
    );
  }

  return (
    <Box>
      {invitations.map((invitation) => (
        <Card
          key={invitation.uuid}
          sx={{
            mb: 2,
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: "none",
          }}
        >
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {invitation.organization.name}
            </Typography>
            <Divider sx={{ my: 1 }} />
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <strong>{t("preferences.enterprise.invitation.role")}:</strong> {getRoleName(invitation.role)}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <strong>{t("preferences.enterprise.invitation.sentAt")}:</strong> {formatDate(invitation.expires_at)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>{t("preferences.enterprise.invitation.expiresAt")}:</strong> {formatDate(invitation.expires_at)}
              </Typography>
              {invitation.invited_by && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  <strong>{t("preferences.invitation.invitedBy")}:</strong> {invitation.invited_by.username}
                </Typography>
              )}
            </Box>
          </CardContent>
          <CardActions sx={{ justifyContent: "flex-end", p: 2 }}>
            <Button size="small" variant="outlined" color="inherit" onClick={() => handleDecline(invitation.uuid)}>
              {t("preferences.enterprise.invitation.decline")}
            </Button>
            <Button
              size="small"
              variant="contained"
              color="primary"
              onClick={() => handleAccept(invitation.uuid, invitation.organization.name)}
            >
              {t("preferences.enterprise.invitation.accept")}
            </Button>
          </CardActions>
        </Card>
      ))}
    </Box>
  );
};

export default InvitationManagement;
